import cx from 'classnames';
import { KeyboardEvent, ReactNode } from 'react';

import type { LandingSection } from 'modules/Authentication/utils/setDefaultLandingPage';
import { IconType } from 'utils/types';

interface LayoutCardProps {
  value: LandingSection;
  selected: boolean;
  saving: boolean;
  onSelect: (section: LandingSection) => void;
  title: string;
  description: string;
  previewContent: ReactNode;
  icon: IconType;
}

const LayoutCard = ({
  value,
  selected,
  saving,
  onSelect,
  title,
  description,
  previewContent,
  icon: Icon,
}: LayoutCardProps) => {
  const handleClick = () => {
    if (saving) return;
    onSelect(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (saving) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(value);
    }
  };

  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={saving}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cx(
        'flex cursor-pointer flex-row items-center',
        'rounded-2xl md:rounded-xl',
        'transition-colors duration-150',
        // Layout: gap stays constant across selection states
        'gap-4 md:gap-2',
        // Desktop becomes a vertical card occupying equal width within the row
        'lg:flex-1 lg:flex-col lg:items-center lg:gap-2',
        // PADDING — identical for selected/unselected to prevent layout shift
        'py-4 pl-4 pr-6',
        'lg:p-4',
        // BORDER WIDTH — identical for selected/unselected at each breakpoint
        //   Mobile: 1px (matches Figma mobile card)
        //   Tablet+: 2px (matches Figma tablet/desktop selected; unselected uses transparent border for stability)
        'border md:border-2',
        // BORDER COLOR — only color changes between states (smoothly via transition-colors)
        selected ? 'border-primaryComp-50 bg-primaryComp-10' : 'border-gray-30 bg-white',
        saving && 'pointer-events-none'
      )}
    >
      {/* Mobile icon pill — hidden at tablet+ */}
      <div
        className={cx(
          'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg md:hidden',
          'transition-colors duration-150',
          selected ? 'bg-primaryComp-50 text-white' : 'bg-gray-20 text-gray-60'
        )}
      >
        <Icon className="h-5 w-5 stroke-current stroke-2" />
      </div>

      {/* Preview area — hidden on mobile, visible at tablet+ */}
      <div className="relative hidden h-[128px] shrink-0 overflow-hidden rounded-lg bg-gray-10 md:block md:w-[182px] lg:w-full">
        {/*
         * Inner 176×128 frame — origin for absolute-positioned skeleton elements.
         * The bottom fade is rendered INSIDE this frame so it stays 176px wide
         * regardless of the outer preview-area width (e.g. on lg:w-full cards).
         */}
        <div className="absolute left-1/2 top-0 h-[128px] w-[176px] -translate-x-1/2 overflow-hidden rounded-[8px] bg-gradient-to-b from-gray-10 from-50% to-transparent">
          {previewContent}
          <div
            className={cx(
              'pointer-events-none absolute bottom-0 left-0 right-0 h-[39px] bg-gradient-to-t transition-colors duration-150',
              selected ? 'from-primaryComp-10' : 'from-white'
            )}
          />
        </div>
      </div>

      {/* Text + radio wrapper: horizontal on mobile/tablet, vertical on desktop */}
      <div className="flex min-w-0 flex-1 flex-row items-center gap-3 lg:w-full lg:flex-col lg:items-center lg:gap-3">
        <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 md:gap-1 lg:w-full lg:items-center">
          <span
            className={cx(
              'font-medium',
              'text-sm md:text-base',
              'transition-colors duration-150',
              selected ? 'text-black md:text-primary-50' : 'text-gray-80'
            )}
          >
            {title}
          </span>
          <span className="text-sm font-light text-gray-60 md:text-xs lg:text-center">{description}</span>
        </div>

        {/*
         * Custom Figma radio button.
         * Mobile (Large size): 24×24 outer, 12×12 dot.
         * Tablet/Desktop (Small size): 20×20 outer, 8×8 dot.
         * Outer ring renders always; the dot only appears when selected.
         */}
        <div
          aria-hidden="true"
          className={cx(
            'flex shrink-0 items-center justify-center rounded-full border bg-white',
            'h-6 w-6 md:h-5 md:w-5',
            'transition-colors duration-150',
            selected ? 'border-primaryComp-50' : 'border-gray-40'
          )}
        >
          <div
            className={cx(
              'rounded-full bg-primaryComp-50',
              'h-3 w-3 md:h-2 md:w-2',
              'transition-opacity duration-150',
              selected ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default LayoutCard;
